// Validacion de datos de una obra antes de guardar (validacion "en origen").
// - errores: impiden guardar, pero solo si afectan a un campo que se está cambiando
//   (o a una obra nueva), para no bloquear ediciones sobre datos historicos.
// - avisos: se muestran y el usuario decide si guarda igualmente.

const MILESTONE_FIELDS = [
    ["zonif", "Zonificación"],
    ["legal", "Legal"],
    ["planoP3", "Plano P3"],
    ["deadlineMob", "Deadline MOB"],
    ["p3Constructora", "P3 constructora"],
    ["p3Mobiliario", "P3 mobiliario"],
    ["fechaIluminacion", "Iluminación"],
    ["fechaSuelo", "Suelo"],
];

const NON_NEGATIVE_FIELDS = [
    ["capex", "CAPEX"],
    ["supCons", "Superficie construida"],
    ["supVta", "Superficie de venta"],
    ["totalOc", "Total OC"],
    ["totalBsk", "Total BSK"],
    ["totalOtros", "Total otros"],
    ["totalInversion", "Total inversión"],
];

// Contadores (en BD son enteros): sin decimales ni negativos.
const WHOLE_COUNT_FIELDS = [
    ["cestas", "Nº de cestas"],
    ["numProbadores", "Nº de probadores"],
    ["totalCajaFisica", "Total caja física"],
    ["totalAcoEstandar", "Total ACO estándar"],
    ["totalAcoReciclador", "Total ACO reciclador"],
];

const isIsoDate = (value) => typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);
const isBlank = (value) => value == null || String(value).trim() === "";
const toNumber = (value) => {
    if (value == null || value === "") return null;
    if (typeof value === "number") return Number.isFinite(value) ? value : null;
    const parsed = Number(String(value).replace(/\./g, "").replace(",", "."));
    return Number.isFinite(parsed) ? parsed : null;
};

export function validateObra(obra = {}) {
    const issues = [];
    const add = (severity, fields, message) => issues.push({ severity, fields, message });

    if (isBlank(obra.tienda)) add("error", ["tienda"], "La tienda es obligatoria.");
    if (isBlank(obra.codigoTienda)) add("error", ["codigoTienda"], "El código de tienda es obligatorio.");
    else if (/^NUEVA-/i.test(String(obra.codigoTienda))) add("warning", ["codigoTienda"], "El código de tienda sigue siendo provisional.");
    // El país se avisa, no se exige: al dar de alta una obra puede no estar decidido
    // todavía (sin país tampoco se puede elegir ubicación). Se completa después.
    if (isBlank(obra.pais)) add("warning", ["pais"], "La obra todavía no tiene país.");

    NON_NEGATIVE_FIELDS.forEach(([field, label]) => {
        const value = toNumber(obra[field]);
        if (value !== null && value < 0) add("error", [field], `${label} no puede ser negativo.`);
    });
    WHOLE_COUNT_FIELDS.forEach(([field, label]) => {
        const value = toNumber(obra[field]);
        if (value !== null && (value < 0 || !Number.isInteger(value))) add("error", [field], `${label} debe ser un número entero sin decimales ni negativo.`);
    });
    const supCons = toNumber(obra.supCons);
    const supVta = toNumber(obra.supVta);
    if (supCons && supVta && supVta > supCons) add("warning", ["supVta", "supCons"], "La superficie de venta supera la construida.");

    if (isIsoDate(obra.inicioObra) && isIsoDate(obra.apertura) && obra.inicioObra > obra.apertura) {
        add("error", ["inicioObra", "apertura"], "El inicio de obra es posterior a la apertura.");
    }
    if (isIsoDate(obra.inicioMontajeMob) && isIsoDate(obra.finMontajeMob) && obra.inicioMontajeMob > obra.finMontajeMob) {
        add("error", ["inicioMontajeMob", "finMontajeMob"], "El montaje de mobiliario empieza después de terminar.");
    }
    if (isIsoDate(obra.apertura) && isIsoDate(obra.finMontajeMob) && obra.finMontajeMob > obra.apertura) {
        add("warning", ["finMontajeMob", "apertura"], "El montaje de mobiliario termina después de la apertura.");
    }
    if (isIsoDate(obra.ventanaInicio) && isIsoDate(obra.ventanaFin) && obra.ventanaInicio > obra.ventanaFin) {
        add("error", ["ventanaInicio", "ventanaFin"], "La ventana empieza después de terminar.");
    }
    if (isIsoDate(obra.apertura)) {
        MILESTONE_FIELDS.forEach(([field, label]) => {
            if (isIsoDate(obra[field]) && obra[field] > obra.apertura) {
                add("warning", [field, "apertura"], `${label} es posterior a la apertura.`);
            }
        });
    }
    return issues;
}

// Mínimo para conservar una obra nueva o duplicada: el nombre de la tienda. El país es
// un aviso, no un requisito: se puede completar después sin perder el alta.
export const NEW_OBRA_REQUIRED_FIELDS = ["tienda"];

// Qué hacer al cerrar la ficha de una obra nueva o duplicada que aún no se ha guardado:
// - "keep": flujo normal (la obra y sus cambios quedan pendientes de guardar).
// - "discard": se descarta sin preguntar: no se ha cambiado nada (una copia sin tocar
//   no se puede conservar).
// - "confirm-discard": hay cambios pero falta el mínimo (tienda y país): se avisa y se
//   descarta si el usuario confirma.
// `drafts`: cambios pendientes de ESA obra ({ field, newValue }).
export function newObraCloseAction(obra, drafts = []) {
    if (!obra?._isNew) return "keep";
    if (!drafts.length) return "discard";
    const valueOf = (field) => {
        const draft = drafts.find((item) => item.field === field);
        return draft ? draft.newValue : obra[field];
    };
    return NEW_OBRA_REQUIRED_FIELDS.every((field) => !isBlank(valueOf(field))) ? "keep" : "confirm-discard";
}

// Separa los problemas relevantes para un guardado: errores que tocan campos
// cambiados (o cualquiera si la obra es nueva) y todos los avisos de esos campos.
export function issuesForSave(obra, changedFields = [], isNew = false) {
    const changed = new Set(changedFields);
    const relevant = validateObra(obra).filter((issue) => isNew || issue.fields.some((field) => changed.has(field)));
    return {
        errors: relevant.filter((issue) => issue.severity === "error"),
        warnings: relevant.filter((issue) => issue.severity === "warning"),
    };
}

// Justificación del panel de cambios (decisión de BSK, 09/2026): obligatoria solo si
// cambia el inicio de obra o la apertura, y en las acciones que no son editar datos
// (archivar o reactivar, eliminar, cambios de catálogo). En el resto es opcional: si
// se deja vacía, el historial guarda «N/A» (la auditoría exige un motivo).
export const REASON_REQUIRED_FIELDS = ["inicioObra", "apertura"];
export const OPTIONAL_REASON = "N/A";
// Tipos de cambio que son edición de datos de la ficha (sin tipo = un solo campo).
const FIELD_EDIT_KINDS = new Set([undefined, null, "", "batch", "alta", "geography"]);

// `drafts`: los cambios que se van a guardar con esta justificación.
export function isReasonRequired(changeRequest, drafts = []) {
    if (!changeRequest) return true;
    if (!FIELD_EDIT_KINDS.has(changeRequest.kind)) return true;
    const fields = changeRequest.kind ? drafts.map((draft) => draft.field) : [changeRequest.field];
    return fields.some((field) => REASON_REQUIRED_FIELDS.includes(field));
}

// Motivo que se guarda: el escrito o, si es opcional y está vacío, «N/A». Vacío si
// falta y era obligatorio (el panel no deja guardar).
export function effectiveReason(reason, required) {
    const text = String(reason ?? "").trim();
    if (text) return text;
    return required ? "" : OPTIONAL_REASON;
}
